import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Music2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUpload } from '../contexts/UploadContext';
import { trackEvent } from '../services/analytics';
import { submitEmailReminder } from '../services/api';

// ─── Post-upload anon-uploader capture ───────────────────────────────────
//
// Renders for anonymous users after at least one file upload has completed.
// Presents an "Inside pastewaves" tile strip (three illustrative previews of
// signed-in surfaces) plus two conversion paths in one calm card:
//
//   Inside strip (always rendered, above Zone 1):
//     Three aspect-square glass tiles previewing signed-in-only surfaces
//     the anon uploader has never seen — /my-files as a workspace, the
//     collection grid, and the /stats page's trend chart. All three are
//     free-tier accessible on sign-in (stats page is Pro-gated for real
//     numbers but free-authed for the surface itself + dummy data), so
//     the tiles honestly preview surfaces the free-signup ask opens up.
//     Artwork lives at /preview/artwork/01.jpg through 06.jpg on the
//     webapp CloudFront origin, uploaded directly to
//     s3://sharewave-prod-webapp/preview/artwork/ and protected from
//     `make sync --delete` by the Makefile's --exclude "preview/*"
//     (added 2026-07-05 in commit 812f3df). The stats tile chart is a
//     hand-rolled SVG monotone bezier — NOT Recharts — so this component
//     doesn't drag Recharts into Home's initial JS bundle. Peak on the
//     rightmost point of STATS_TREND matches the footer "N today" number.
//
//   Zone 1 (primary, always rendered):
//     Sign in with Google → cryptographic file-claim → files owned by the
//     new account AND TTL resets to 14 days from claim moment. The "14 days
//     from today" language in the sub-copy is the real payoff — Gilfoyle
//     verified handleClaimFile at lambda-file-metadata/main.go:2886 resets
//     the TTL to now + 14d, not extends-in-place.
//
//   Zone 2 (secondary, feature-flag gated):
//     Leave an email → 48h pre-expiry reminder → link back into the same
//     sign-in-and-claim flow. Lighter-touch capture for readers who won't
//     sign up in-session. Gated behind REACT_APP_EMAIL_REMINDER_ENABLED so
//     the entire zone stays out of the DOM until Gilfoyle ships the backend
//     Lambda + DDB row + SES cron. The api.ts stub throws until then.
//
// Voice constraints locked with Glenn 2026-07-02:
//   - No exclamation marks, no sparkle icons, no "Free" pill spam.
//   - No infinite pulse/rotate animations. Motion is enter/exit only.
//   - No engineer-nouns ("Usage Analytics" / "Storage Stats") — the anon
//     first-timer has no dashboard-need to shout about.
//   - Zone 2 promise is a hard commitment: ONE reminder, and this email
//     list is never reused for product updates. Preserving that promise is
//     load-bearing; if a future broadcast surface exists, it opts users in
//     from a different entry point.
//
// Preserved from the prior (heavier) implementation:
//   - Session-storage dismiss with re-open on new upload completion.
//     Business logic; users forgive one dismiss but expect the prompt to
//     come back when they upload again.
//   - `claim_prompt_shown` telemetry (Donald's L2 funnel numerator).
//   - `claimSigninClicked` localStorage buffer flushed by AuthContext
//     post-login (upper-funnel instrumentation).
//   - Poll fallback so uploads that complete outside completedFiles-context
//     updates still surface the card within ~2s.
// ─────────────────────────────────────────────────────────────────────────

// ─── Inside-preview tile machinery ──────────────────────────────────────
// Six artwork slots. Collection tile uses 01–04, file organiser uses
// 04–06. Overlap at slot 04 is intentional so a track in the file list
// also shows up in the collection artwork — the natural story of "these
// tracks live in this collection." Sources: s3://sharewave-prod-webapp/
// preview/artwork/*.jpg, served through CloudFront at /preview/artwork/*.
const ARTWORK_SLOTS = [
  { src: '/preview/artwork/01.jpg', fallback: 'bg-gradient-to-br from-soundwave-600 to-soundwave-800' },
  { src: '/preview/artwork/02.jpg', fallback: 'bg-gradient-to-br from-neon-500 to-neon-700' },
  { src: '/preview/artwork/03.jpg', fallback: 'bg-gradient-to-br from-rose-500 to-purple-700' },
  { src: '/preview/artwork/04.jpg', fallback: 'bg-gradient-to-br from-amber-400 to-orange-600' },
  { src: '/preview/artwork/05.jpg', fallback: 'bg-gradient-to-br from-emerald-500 to-teal-700' },
  { src: '/preview/artwork/06.jpg', fallback: 'bg-gradient-to-br from-indigo-500 to-violet-700' },
];

// Small helper: gradient-fallback div with an <img> layered on top. img
// hides itself on error so the gradient shows through — the tiles look
// styled even if a JPG is temporarily missing or slow to load.
const ArtworkCell: React.FC<{ src: string; fallback: string; className?: string }> = ({
  src,
  fallback,
  className = '',
}) => (
  <div className={`relative overflow-hidden ${fallback} ${className}`}>
    <img
      src={src}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover"
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.display = 'none';
      }}
    />
  </div>
);

// Plausible hobbyist-musician WIP names. Not any real user's; kept
// generic enough that anyone in the target segment (bedroom producer,
// band member, piano practice logger) reads them as familiar.
const FILE_ROWS: { title: string; duration: string; slot: number }[] = [
  { title: 'Bassline sketch', duration: '3:12', slot: 3 }, // 04.jpg
  { title: 'Bridge idea v2',  duration: '1:47', slot: 4 }, // 05.jpg
  { title: 'Piano loop 04',   duration: '2:38', slot: 5 }, // 06.jpg
];

// Deterministic 7-day daily-views series for the stats tile. Peak is
// rightmost so the footer "N today" summary matches the last data
// point in the chart. NOT a live fetch — this is illustrative, honest
// about being a preview of the surface rather than a promise of
// specific numbers.
const STATS_TREND = [
  { day: 'Mon', views: 12 },
  { day: 'Tue', views: 18 },
  { day: 'Wed', views: 25 },
  { day: 'Thu', views: 15 },
  { day: 'Fri', views: 30 },
  { day: 'Sat', views: 22 },
  { day: 'Sun', views: 48 },
];
const STATS_TOTAL_7D = STATS_TREND.reduce((s, d) => s + d.views, 0);
const STATS_TODAY = STATS_TREND[STATS_TREND.length - 1].views;

// Catmull-Rom → cubic bezier so the 7-point trend curve reads smooth
// instead of angular. Hand-rolled to keep Recharts (~50KB gzipped) out
// of Home's initial bundle. Same visual grammar as StatsPage's
// AreaChart — monotone curve + soundwave gradient fill — just at
// aspect-square tile scale with no axes/grid/tooltip chrome.
const STATS_MAX = Math.max(...STATS_TREND.map((d) => d.views));
const STATS_POINTS = STATS_TREND.map((d, i) => ({
  x: (i / (STATS_TREND.length - 1)) * 100,
  y: 100 - (d.views / STATS_MAX) * 80,
}));
const buildSmoothPath = (pts: { x: number; y: number }[]) => {
  const path = [`M${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}`];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || pts[i + 1];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    path.push(
      `C${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`,
    );
  }
  return path.join(' ');
};
const STATS_LINE_PATH = buildSmoothPath(STATS_POINTS);
const STATS_AREA_PATH = `${STATS_LINE_PATH} L100,100 L0,100 Z`;

// ─── Tile 1 — File organiser ────────────────────────────────────────────
// Stands in for /my-files. Small "MY FILES" header + Music2 icon; three
// rows mimic RecentFiles' [artwork thumb][name][duration] pattern
// without the destructive per-row chrome (Trash2/Clipboard) — those
// read as management affordances that fight the preview framing.
const FileOrganiserTile: React.FC = () => (
  <div className="aspect-square rounded-xl bg-black/40 backdrop-blur-xl border border-white/10 overflow-hidden flex flex-col">
    <div className="px-3 pt-3 pb-2 flex items-center gap-2">
      <Music2 className="w-3.5 h-3.5 text-white/50 flex-shrink-0" />
      <div className="text-[10px] md:text-[11px] uppercase tracking-wider text-white/60 font-medium">
        My files
      </div>
    </div>
    <div className="flex-1 px-2 pb-2 flex flex-col justify-center gap-1.5">
      {FILE_ROWS.map((row, i) => {
        const art = ARTWORK_SLOTS[row.slot];
        return (
          <div
            key={i}
            className="flex items-center gap-2 px-1.5 py-1.5 rounded-md hover:bg-white/[0.02] transition-colors"
          >
            <ArtworkCell
              src={art.src}
              fallback={art.fallback}
              className="w-9 h-9 md:w-10 md:h-10 rounded-md flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] md:text-xs text-white/85 font-medium truncate">
                {row.title}
              </div>
            </div>
            <div className="text-[10px] md:text-[11px] text-white/40 tabular-nums flex-shrink-0">
              {row.duration}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ─── Tile 2 — Collections ───────────────────────────────────────────────
// Stands in for /collection/:id and the /@handle grid. Full-tile 2×2
// mosaic with real artwork samples, overlaid title + track count.
// Mirrors CollectionArtMosaic's geometry.
const CollectionsTile: React.FC = () => (
  <div className="aspect-square rounded-xl bg-black/40 backdrop-blur-xl border border-white/10 overflow-hidden relative">
    <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
      {ARTWORK_SLOTS.slice(0, 4).map((slot, i) => (
        <ArtworkCell
          key={i}
          src={slot.src}
          fallback={slot.fallback}
          className="w-full h-full"
        />
      ))}
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 p-3">
      <div className="text-[11px] md:text-xs text-white/95 font-medium truncate">
        Late-night takes
      </div>
      <div className="text-[10px] md:text-[11px] text-white/70 truncate">
        4 tracks
      </div>
    </div>
  </div>
);

// ─── Tile 3 — Track stats ───────────────────────────────────────────────
// Stands in for /stats/:fileId. Hand-rolled SVG area chart (see
// buildSmoothPath above) matches StatsPage's visual grammar (monotone
// curve + soundwave gradient fill) without dragging Recharts into
// Home's bundle. Header + footer mirror StatsPage's uppercase label +
// hero-metric numeral treatment at tile scale.
const StatsTile: React.FC = () => (
  <div className="aspect-square rounded-xl bg-black/40 backdrop-blur-xl border border-white/10 overflow-hidden flex flex-col">
    <div className="px-3 pt-3 pb-1 flex items-center justify-between">
      <div className="text-[10px] md:text-[11px] uppercase tracking-wider text-white/60 font-medium">
        Plays this week
      </div>
      <div className="text-[10px] md:text-[11px] text-white/40 tabular-nums">
        {STATS_TOTAL_7D}
      </div>
    </div>
    <div className="flex-1 min-h-0 relative">
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="claim-stats-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff4800" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#ff4800" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={STATS_AREA_PATH} fill="url(#claim-stats-fill)" />
        <path
          d={STATS_LINE_PATH}
          fill="none"
          stroke="#ff4800"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
    <div className="px-3 pb-3 pt-1 flex items-baseline gap-1.5">
      <div className="text-lg md:text-xl font-bold text-soundwave-500 tabular-nums leading-none">
        {STATS_TODAY}
      </div>
      <div className="text-[10px] md:text-[11px] text-white/50">today</div>
    </div>
  </div>
);

const INSIDE_TILES: { key: string; node: React.ReactNode; caption: string }[] = [
  { key: 'organiser',   node: <FileOrganiserTile />, caption: 'Add artwork to your tracks, organize, sort and search.' },
  { key: 'collections', node: <CollectionsTile />,   caption: 'Group tracks into collections.' },
  { key: 'stats',       node: <StatsTile />,         caption: 'See how your tracks are played.' },
];

interface PendingClaim {
  fileId: string;
  claimToken: string;
  fileName: string;
  timestamp: number;
}

// URL-presence gate: the whole Zone 2 email-capture affordance renders only
// when REACT_APP_EMAIL_REMINDER_URL is set. One env var, no drift between a
// "feature enabled" boolean and an "endpoint URL" — either the backend is
// wired or it isn't. Gilfoyle dark-launches by omitting the env var until
// the Lambda + DDB + SES cron are green.
const EMAIL_REMINDER_URL = process.env.REACT_APP_EMAIL_REMINDER_URL;

// RFC-lite email shape check — strict enough to catch typos ("user@gmailcom"),
// loose enough not to reject legit ccTLD or plus-address forms.
const looksLikeEmail = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export const SignInToClaimBanner: React.FC = () => {
  const { isAuthenticated, signIn, getValidToken } = useAuth();
  const { completedFiles } = useUpload();
  const [pendingClaims, setPendingClaims] = useState<PendingClaim[]>([]);
  const [isDismissed, setIsDismissed] = useState(() => {
    return sessionStorage.getItem('claimBannerDismissed') === 'true';
  });

  // Email-capture state (only meaningful when EMAIL_REMINDER_ENABLED).
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Guard so the visibility-tracking event fires at most once per mount,
  // even though `loadPendingClaims` polls every 2s.
  const claimPromptTrackedRef = useRef<boolean>(false);

  const loadPendingClaims = useCallback(() => {
    if (isAuthenticated || isDismissed) {
      setPendingClaims([]);
      return;
    }

    try {
      const storedClaims = localStorage.getItem('pendingClaims');
      if (storedClaims) {
        const claims: PendingClaim[] = JSON.parse(storedClaims);
        const validClaims = claims.filter(
          claim => Date.now() - claim.timestamp < 24 * 60 * 60 * 1000
        );
        if (validClaims.length !== claims.length) {
          localStorage.setItem('pendingClaims', JSON.stringify(validClaims));
        }
        setPendingClaims(validClaims);
      } else {
        setPendingClaims([]);
      }
    } catch (error) {
      console.error('Failed to load pending claims:', error);
    }
  }, [isAuthenticated, isDismissed]);

  useEffect(() => {
    loadPendingClaims();
  }, [loadPendingClaims]);

  useEffect(() => {
    if (isAuthenticated) return;

    if (completedFiles > 0) {
      // Un-dismiss when new uploads complete — a fresh drop earns another
      // prompt even if the user dismissed a previous instance in the same
      // session.
      if (sessionStorage.getItem('claimBannerDismissed') === 'true') {
        sessionStorage.removeItem('claimBannerDismissed');
        setIsDismissed(false);
      }
      const timer = setTimeout(() => loadPendingClaims(), 100);
      return () => clearTimeout(timer);
    }

    const pollInterval = setInterval(() => loadPendingClaims(), 2000);
    return () => clearInterval(pollInterval);
  }, [completedFiles, isAuthenticated, loadPendingClaims]);

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('claimBannerDismissed', 'true');
  };

  const handleSignIn = () => {
    try {
      localStorage.setItem('claimSigninClicked', String(Date.now()));
    } catch {
      // localStorage can throw in private mode; never break sign-in.
    }
    signIn();
  };

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (emailStatus === 'submitting' || emailStatus === 'done') return;
    if (!looksLikeEmail(email)) {
      setEmailError('That doesn’t look like an email.');
      return;
    }
    setEmailStatus('submitting');
    setEmailError(null);
    try {
      const fileIds = pendingClaims.map(c => c.fileId);
      await submitEmailReminder(email.trim(), fileIds);
      setEmailStatus('done');
      // Funnel signal — Donald's Zone-2 numerator. Uploader is anonymous
      // (that's the whole capture premise), so allowAnon is required. The
      // backend allowlist in lambda-events/main.go must accept this event
      // AND permit an anon caller; without both, the beacon is silently
      // dropped at the API edge.
      trackEvent('email_reminder_captured', getValidToken, { allowAnon: true });
    } catch (err) {
      console.error('email reminder submit failed:', err);
      setEmailStatus('error');
      setEmailError(err instanceof Error ? err.message : 'Something went wrong. Try again in a moment.');
    }
  };

  // Fire `claim_prompt_shown` exactly once per mount, the first time the
  // visibility gate would pass. Mirrors the render gate so the event
  // tracks the actual "banner became visible" moment.
  // allowAnon: true is load-bearing — this banner ONLY renders for anon
  // users (that's the whole premise), so without allowAnon the beacon
  // gets rejected at the API edge for lack of auth. Prior code omitted
  // the flag and silently dropped every visibility event; 30d
  // CloudWatch shows 0 across all users despite N=38 clicks + N=48
  // claim completions. Fixed 2026-07-05.
  useEffect(() => {
    if (claimPromptTrackedRef.current) return;
    if (isAuthenticated || isDismissed) return;
    if (pendingClaims.length === 0) return;
    if (sessionStorage.getItem('claimBannerDismissed') === 'true') return;
    claimPromptTrackedRef.current = true;
    trackEvent('claim_prompt_shown', getValidToken, { allowAnon: true });
  }, [isAuthenticated, isDismissed, pendingClaims, getValidToken]);

  if (
    isAuthenticated ||
    isDismissed ||
    pendingClaims.length === 0 ||
    sessionStorage.getItem('claimBannerDismissed') === 'true'
  ) {
    return null;
  }

  const fileCount = pendingClaims.length;
  const fileWord = fileCount === 1 ? 'file' : 'files';
  const themWord = fileCount === 1 ? 'it' : 'them';

  return (
    <motion.div
      className="relative overflow-hidden rounded-2xl bg-white/[0.02] border border-white/8 px-5 py-5 md:px-6 md:py-6"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      {/* Dismiss X — top-right, quiet. Absolute-positioned so the two-zone
          stack below can breathe without reserving a column for it. */}
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      {/* ─── Inside preview strip ────────────────────────────────────────── */}
      <div className="pr-8">
        <div className="text-[11px] uppercase tracking-wider text-white/40 font-medium mb-3">
          Inside pastewaves
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 max-w-3xl">
          {INSIDE_TILES.map(({ key, node, caption }, i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 + i * 0.06, ease: 'easeOut' }}
            >
              {node}
              <div className="mt-2 text-[11px] md:text-xs text-white/55 leading-snug">
                {caption}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ─── Zone 1 — Sign in and keep them ─────────────────────────────── */}
      <div className="mt-6 pt-5 border-t border-white/8 pr-8">
        <h3 className="text-lg font-semibold text-white leading-tight">
          Keep your uploads.
        </h3>
        <p className="mt-1 text-sm text-white/60 leading-relaxed max-w-prose">
          Signing in claims your {fileWord} and keeps {themWord}{' '}
          <span className="text-white/80 font-medium">another 14 days from today</span>.
        </p>
        <button
          onClick={handleSignIn}
          className="mt-3 inline-flex items-center px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-soundwave-500 hover:bg-soundwave-600 transition-colors"
        >
          Sign in with Google
        </button>
      </div>

      {/* ─── Zone 2 — Or leave an email (URL-presence gated) ────────────── */}
      {EMAIL_REMINDER_URL && (
        <div className="mt-5 pt-5 border-t border-white/8">
          <div className="text-xs text-white/50 tracking-wide uppercase font-medium">
            Not signing in?
          </div>

          {emailStatus === 'done' ? (
            <div className="mt-2 flex items-center gap-2 text-sm text-white/70">
              <Check className="w-4 h-4 text-soundwave-400 flex-shrink-0" />
              <span>Got it. We’ll email you before your {fileWord} expire.</span>
            </div>
          ) : (
            <>
              <p className="mt-1 text-sm text-white/70 leading-relaxed max-w-prose">
                Leave an email. We’ll send{' '}
                <span className="text-white font-medium">one</span> reminder 48 hours
                before your {fileWord} would expire — and that’s the only email you’ll
                get from us.
              </p>
              <form onSubmit={handleEmailSubmit} className="mt-3 flex items-stretch gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  placeholder="you@example.com"
                  disabled={emailStatus === 'submitting'}
                  aria-label="Your email"
                  className="flex-1 min-w-0 h-10 px-3 rounded-xl bg-white/[0.03] border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-soundwave-500/50 focus:bg-white/[0.05] transition-colors disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={emailStatus === 'submitting' || !email.trim()}
                  className="h-10 px-4 rounded-xl text-sm font-medium text-white/80 bg-white/[0.05] hover:bg-white/[0.10] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {emailStatus === 'submitting' ? 'Sending…' : 'Remind me'}
                </button>
              </form>
              {emailError && (
                <div className="mt-2 text-xs text-red-300/80">{emailError}</div>
              )}
            </>
          )}
        </div>
      )}
    </motion.div>
  );
};
